(() => {
  'use strict';

  if (window.RudcaOriginBrowser) return;

  const previewKeys = ['preview_theme_id', '_fd', 'pb'];
  const instances = new WeakMap();

  class OriginBrowser {
    constructor(element) {
      this.element = element;
      this.grid = element.querySelector('[data-origin-grid]');
      this.next = element.querySelector('[data-origin-next]');
      this.status = element.querySelector('[data-origin-status]');
      this.empty = element.querySelector('[data-origin-empty]');
      this.sourcePage = Number(element.dataset.sourcePage);
      this.ids = new Set(Array.from(this.grid.querySelectorAll('[data-origin-item]'), item => item.dataset.productId));
      this.visited = new Set();
      this.busy = false;
      this.failed = false;
      this.destroyed = false;
      this.currentLocation = new URL(window.location.href);
      this.base = new URL(element.dataset.originBaseUrl, window.location.origin);
      element.querySelectorAll('[data-origin-next][href], [data-origin-previous][href]').forEach(link => this.preservePreview(link));
      this.onClick = this.onClick.bind(this);
      this.next.addEventListener('click', this.onClick);
      if (this.ids.size < 12 && this.next.hasAttribute('href')) this.load(12);
    }

    preservePreview(link) {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      previewKeys.forEach(key => {
        if (this.currentLocation.searchParams.has(key)) url.searchParams.set(key, this.currentLocation.searchParams.get(key));
      });
      link.href = url.pathname + url.search;
    }

    pageUrl(href, afterPage) {
      const url = new URL(href, window.location.origin);
      const page = Number(url.searchParams.get('page'));
      if (url.origin !== window.location.origin || url.pathname !== this.base.pathname || url.searchParams.get('view') !== this.element.dataset.originView || !Number.isSafeInteger(page) || page <= afterPage) throw new Error('Invalid next page');
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      if (this.visited.has(url.href)) throw new Error('Repeated next page');
      return url;
    }

    nextUrl() {
      if (!this.next.hasAttribute('href') || this.next.hidden) return null;
      return this.pageUrl(this.next.href, this.sourcePage);
    }

    onClick(event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (this.failed) return;
      event.preventDefault();
      if (!this.busy) this.load(this.ids.size + 12);
    }

    async load(target) {
      if (this.busy || this.failed || this.destroyed) return;
      this.busy = true;
      this.element.setAttribute('aria-busy', 'true');
      this.next.setAttribute('aria-disabled', 'true');
      this.status.textContent = this.element.dataset.textLoading;
      let hasError = false;
      let focusStatus = false;
      try {
        for (let round = 0; round < 4 && this.ids.size < target; round += 1) {
          const next = this.nextUrl();
          if (!next || this.destroyed) break;
          this.visited.add(next.href);
          const requestedPage = Number(next.searchParams.get('page'));
          const requestUrl = new URL(next.href);
          requestUrl.searchParams.set('section_id', this.element.dataset.originSectionId);
          this.controller = new AbortController();
          const timeout = window.setTimeout(() => this.controller.abort(), 12000);
          let markup;
          try {
            const response = await fetch(requestUrl.href, {credentials: 'same-origin', signal: this.controller.signal});
            if (!response.ok) throw new Error('Section request failed');
            markup = await response.text();
          } finally {
            window.clearTimeout(timeout);
          }
          if (this.destroyed) break;
          const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
          const incoming = documentFragment.querySelector('[data-rudca-origin-browser]');
          if (!incoming || incoming.dataset.origin !== this.element.dataset.origin || incoming.dataset.originView !== this.element.dataset.originView || incoming.dataset.originSectionId !== this.element.dataset.originSectionId || Number(incoming.dataset.sourcePage) !== requestedPage) throw new Error('Unexpected section response');
          const incomingGrid = incoming.querySelector('[data-origin-grid]');
          const incomingNext = incoming.querySelector('[data-origin-next]');
          if (!incomingGrid || !incomingNext) throw new Error('Incomplete section response');
          const nextHref = incomingNext.hasAttribute('href') && !incomingNext.hidden ? this.pageUrl(incomingNext.getAttribute('href'), requestedPage) : null;
          const additions = document.createDocumentFragment();
          incomingGrid.querySelectorAll('[data-origin-item]').forEach(item => {
            const id = item.dataset.productId;
            if (!/^\d+$/.test(id || '') || item.dataset.productOrigin !== this.element.dataset.origin || this.ids.has(id)) return;
            this.ids.add(id);
            additions.appendChild(document.importNode(item, true));
          });
          this.grid.appendChild(additions);
          this.sourcePage = requestedPage;
          this.element.dataset.sourcePage = String(requestedPage);
          if (nextHref) {
            this.next.setAttribute('href', nextHref.pathname + nextHref.search);
            this.preservePreview(this.next);
            this.next.hidden = false;
            this.nextUrl();
          } else {
            focusStatus = document.activeElement === this.next;
            this.next.removeAttribute('href');
            this.next.hidden = true;
          }
          this.empty.hidden = this.ids.size > 0;
        }
      } catch (error) {
        if (!this.destroyed) {
          hasError = true;
          this.failed = true;
          this.status.textContent = this.element.dataset.textError;
        }
      } finally {
        this.busy = false;
        this.element.removeAttribute('aria-busy');
        this.next.removeAttribute('aria-disabled');
        if (!hasError && !this.destroyed) {
          const shown = this.element.dataset.textShown.replace('[count]', String(this.ids.size));
          this.status.textContent = this.next.hidden ? shown + ' · ' + this.element.dataset.textComplete : shown;
          if (focusStatus) {
            this.status.tabIndex = -1;
            this.status.focus({preventScroll: true});
          }
        }
      }
    }

    destroy() {
      this.destroyed = true;
      if (this.controller) this.controller.abort();
      this.next.removeEventListener('click', this.onClick);
    }
  }

  function mount(root = document) {
    root.querySelectorAll('[data-rudca-origin-browser]').forEach(element => {
      if (!instances.has(element)) instances.set(element, new OriginBrowser(element));
    });
  }

  window.RudcaOriginBrowser = {mount};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), {once: true});
  else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-rudca-origin-browser]').forEach(element => {
      const instance = instances.get(element);
      if (instance) instance.destroy();
      instances.delete(element);
    });
  });
})();
